import { Star } from "lucide-react";
import { useLanguage } from "@/lib/i18n";
import { AppStoreButton } from "./AppStoreButton";
import { useLandingConfig, useLandingCopy } from "./LandingContext";

/**
 * Landing hero — light, cream, single-column and centered (sociallite.app
 * style). Audience-agnostic: the copy and the optional hero photo come from
 * the landing config; without a photo it falls back to the app mockup.
 *
 * Mobile-first: headline, subtitle, CTA and the App Store rating badge all fit
 * in the first viewport at 375px; the visual is capped in height and comes
 * after the CTA block.
 */
export function ParentsHero() {
  const { language } = useLanguage();
  const copy = useLandingCopy();
  const { heroImage } = useLandingConfig();
  const mockup = language === "fr" ? "/mockup-fr.png" : "/mockup-en.png";

  return (
    <section className="bg-background">
      <div className="mx-auto max-w-2xl px-4 pb-12 pt-20 text-center sm:px-6 sm:pb-16 sm:pt-28">
        <h1 className="font-heading text-4xl font-bold leading-[1.08] tracking-tight text-foreground sm:text-5xl">
          {copy.hero.title}
        </h1>

        <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-muted-foreground sm:mt-5 sm:text-lg">
          {copy.hero.subtitle}
        </p>

        <div className="mt-6 flex flex-col items-center gap-3 sm:mt-8">
          <AppStoreButton
            location="hero"
            label={copy.hero.cta}
            variant="button"
            className="min-h-[52px] w-full px-7 py-4 text-lg sm:w-auto sm:min-w-[280px]"
          />
          {/* Store badges (App Store + Google Play): desktop reinforcement only —
              on mobile the rating badge below carries the trust and keeps the
              fold tight. */}
          <AppStoreButton
            location="hero_badge"
            label={copy.hero.cta}
            className="hidden sm:flex"
          />
        </div>

        {/* App Store rating — laurel-style trust badge, right under the CTA */}
        <div className="mt-4 flex justify-center sm:mt-5">
          <div className="inline-flex items-center gap-2.5 rounded-full border border-border bg-card px-4 py-2 shadow-sm">
            <span className="flex items-center gap-0.5 text-accent" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="h-4 w-4 fill-current" />
              ))}
            </span>
            <span className="font-heading text-lg font-bold leading-none text-foreground">
              {copy.rating.value}
            </span>
            <span className="text-sm leading-none text-muted-foreground">
              {copy.rating.label}
            </span>
          </div>
        </div>

        <p className="mt-3 text-xs text-muted-foreground sm:mt-4 sm:text-sm">
          {copy.hero.reassurance}
        </p>

        {/* Visual below the CTA block, centered. Height-capped on mobile so
            the fold stays copy + CTA. */}
        <div className="mt-8 flex justify-center sm:mt-12">
          {heroImage ? (
            <img
              src={heroImage}
              alt={copy.hero.familyAlt}
              loading="eager"
              className="h-[360px] w-[280px] rounded-[2rem] object-cover shadow-md sm:h-[440px] sm:w-[340px]"
            />
          ) : (
            <div className="rounded-[2.25rem] border border-border bg-card p-2.5 shadow-md">
              <img
                src={mockup}
                alt={copy.hero.mockupAlt}
                width={272}
                height={588}
                loading="eager"
                className="h-[360px] w-auto rounded-[1.75rem] sm:h-[480px]"
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
